import React, { useEffect, useMemo, useState } from "react";
import { Grid, Box, CircularProgress, Button } from "@mui/material";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from "@dnd-kit/core";

import { Document } from "../../../Models/company";
import { CardDoc } from "./CardDoc";
import { AvailableDocsList } from "./AvailableDocsList";
import { AnnexSection } from "./AnnexSection";
import { PredefinedAnnexSection } from "./PredefinedAnnexSection";
import { useDocuments } from "../../../Query/useCompanies";
import { usePredefinedSections } from "../../../Query/usePredefinedSections";
import { AnnexDocument, useAnnexActions } from "../../../Core/useAnnexActions";
import { usePredefinedSectionActions } from "../../../Core/usePredefinedSectionActions";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  applyCustomIds,
  applyOrder,
  filterAvailableDocuments,
} from "../../../Helpers/annexActions";
import colors from "../../../Constants/colors";
import { useAnnexCleanup } from "../../../Core/useAnnexSync";
import { ComoButton } from "../../Common/ComoButton";
import { useTranslation } from "react-i18next";

interface AnnexBodyProps {
  companyId: number;
  showPlaceholders: boolean;
  annex: AnnexDocument[];
  setAnnex: React.Dispatch<React.SetStateAction<AnnexDocument[]>>;
  predefinedAnnex: Record<number, AnnexDocument[]>;
  setPredefinedAnnex: React.Dispatch<
    React.SetStateAction<Record<number, AnnexDocument[]>>
  >;
  selectedPredefinedSection: number | null;
  setSelectedPredefinedSection: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  handleNextStep: () => void;
  isStepAccessible: (stepIndex: number) => boolean;
  currentStep: number;
}

export function AnnexBody({
  companyId,
  showPlaceholders,
  annex,
  setAnnex,
  predefinedAnnex,
  setPredefinedAnnex,
  selectedPredefinedSection,
  setSelectedPredefinedSection,
  handleNextStep,
  isStepAccessible,
  currentStep,
}: AnnexBodyProps) {
  const MAX_LEFT = 12;
  const MAX_RIGHT = 4;
  const [draggedDoc, setDraggedDoc] = useState<AnnexDocument | null>(null);
  const { t } = useTranslation("evaluationPage");

  const { data, isLoading: isLoadingDocuments } = useDocuments(
    companyId.toString(),
    1,
    1000,
    "name",
    "asc",
    true,
  );

  const { data: predefinedData, isLoading: isLoadingPredefinedData } =
    usePredefinedSections(undefined, showPlaceholders);

  const predefinedSection = useMemo(() => {
    return applyCustomIds(
      applyOrder(
        predefinedData?.appendix_structures?.find(
          (section) => section.id === selectedPredefinedSection,
        )?.structure || [],
      ),
    );
  }, [selectedPredefinedSection, predefinedData]);

  const predefinedSectionIds = useMemo(
    () =>
      predefinedData?.appendix_structures?.map((section) => section.id) || [],
    [predefinedData],
  );

  const {
    addAvailableDocument,
    removeDocument,
    updateDocument,
    swapDocuments,
    addSection,
    addAvailableSubsection,
    addAnnexSubsection,
  } = useAnnexActions(setAnnex);

  const {
    replacePredefinedSection,
    removePredefinedDocument,
    updatePredefinedDocument,
    addAvailablePredefinedDocument,
  } = usePredefinedSectionActions(
    setPredefinedAnnex,
    predefinedSection,
    selectedPredefinedSection,
  );

  const availableDocs = showPlaceholders
    ? filterAvailableDocuments(
        predefinedAnnex[selectedPredefinedSection || 0],
        data?.documents,
      )
    : filterAvailableDocuments(annex, data?.documents);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const handleSelectPredefinedSection = (sectionId: number | null) => {
    setSelectedPredefinedSection(sectionId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.doc) {
      setDraggedDoc(active.data.current.doc);
    }
  };

  const handleAvailableDocDrop = (
    availableDoc: Document,
    overType?: string,
    overId?: number | string,
  ) => {
    if (overType === "annex" && typeof overId === "number") {
      addAvailableDocument(availableDoc, overId);
      return;
    }

    if (overType === "subsection" && typeof overId === "number") {
      addAvailableSubsection(overId!, availableDoc);
      return;
    }

    if (overType === "zone") {
      addAvailableDocument(availableDoc);
      return;
    }

    if (overType === "predefined" && typeof overId === "string") {
      replacePredefinedSection(overId!, { availableDoc });
      return;
    }
  };

  const handleAnnexDocDrop = (
    activeId?: number,
    overType?: string,
    overId?: number | string,
  ) => {
    if (overType === "annex" && typeof overId === "number") {
      swapDocuments(activeId!, overId!);
      return;
    }

    if (overType === "subsection" && typeof overId === "number") {
      addAnnexSubsection(overId!, activeId!);
      return;
    }

    if (overType === "predefined" && typeof overId === "string") {
      replacePredefinedSection(overId!, { docId: activeId });
      return;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedDoc(null);

    if (!over) {
      return;
    }

    const activeCompositeId = active.id.toString().split("-");
    const overCompositeId = over.id.toString().split("-");
    const activeDoc = active.data.current?.doc;

    const overType = overCompositeId.pop();
    const activeType = activeCompositeId.shift();

    const overId = over.data.current?.id;
    const activeId = active.data.current?.doc.id;

    if (activeType === "available" && activeDoc) {
      handleAvailableDocDrop(activeDoc, overType, overId);
      return;
    }

    if (activeType === "annex") {
      handleAnnexDocDrop(activeId, overType, overId);
      return;
    }
  };

  useEffect(() => {
    if (predefinedSectionIds.length === 1) {
      setSelectedPredefinedSection(predefinedSectionIds[0]);
    }
  }, [predefinedSectionIds, setSelectedPredefinedSection]);

  useAnnexCleanup({
    selectedPredefinedSection,
    predefinedSection,
    removeDocument,
    removePredefinedDocument,
    predefinedAnnex,
    predefinedData,
    isLoadingPredefinedData,
    annex,
    documents: data,
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Grid container spacing={4} sx={{ maxWidth: 1440, width: "100%" }}>
        <Grid item xs={12} md={6}>
          {isLoadingDocuments ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress sx={{ color: colors.primary_dark }} />
            </Box>
          ) : (
            <AvailableDocsList
              availableDocs={availableDocs}
              onAddDocument={
                showPlaceholders
                  ? addAvailablePredefinedDocument
                  : addAvailableDocument
              }
              maxShown={MAX_LEFT}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {showPlaceholders ? (
            <PredefinedAnnexSection
              isLoadingPredefinedData={isLoadingPredefinedData}
              predefinedSectionIds={predefinedSectionIds}
              predefinedSections={predefinedSection}
              selectedPredefinedSection={selectedPredefinedSection}
              predefinedAnnex={predefinedAnnex}
              onRemoveDocument={removePredefinedDocument}
              onUpdateDocument={updatePredefinedDocument}
              onReplacePredefinedSection={replacePredefinedSection}
              onSelectPredefinedSection={handleSelectPredefinedSection}
            />
          ) : (
            <AnnexSection
              orderedAnnex={annex}
              onRemoveDocument={removeDocument}
              onUpdateDocument={updateDocument}
              onAddSection={addSection}
              maxShown={MAX_RIGHT}
              draggedDoc={draggedDoc}
            />
          )}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <ComoButton
              onClick={handleNextStep}
              disabled={!isStepAccessible(currentStep + 1)}
            >
              {t("evaluationPage.next")}
            </ComoButton>
          </Box>
        </Grid>
      </Grid>

      <DragOverlay modifiers={[snapCenterToCursor]}>
        {draggedDoc && (
          <Box
            sx={{
              width: 200,
            }}
          >
            <CardDoc doc={draggedDoc} active />
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
}
